/**
 * Terms of Service Page
 * 
 * Legal terms for using the Pleasance platform.
 * Required for production deployment.
 */

import React from 'react';

function TermsPage() {
    return (
        <div className="min-h-screen bg-charcoal px-4 py-12">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <h1 className="text-4xl font-serif text-wine-400 mb-2">Terms of Service</h1>
                <p className="text-ivory-500 text-sm mb-8">Last updated: January 18, 2026</p>

                <div className="prose prose-invert prose-wine max-w-none space-y-8 text-ivory-300">

                    {/* Section 1 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">1. Acceptance of Terms</h2>
                        <p>
                            By accessing or using Pleasance ("the Platform"), you agree to be bound by these Terms of Service.
                            If you do not agree to these terms, you may not access or use the Platform.
                        </p>
                    </section>

                    {/* Section 2 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">2. Eligibility</h2>
                        <p>
                            You must be <span className="text-wine-400 font-semibold">at least 18 years of age</span> to access this Platform.
                            By using this Platform, you represent and warrant that you are at least 18 years old and that
                            accessing adult content is legal in your jurisdiction.
                        </p>
                    </section>

                    {/* Section 3 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">3. Nature of Content</h2>
                        <p>
                            Pleasance is an <strong>educational resource</strong> about human sexuality, kinks, BDSM practices,
                            and related topics. The Platform provides information for educational purposes only.
                        </p>
                        <p className="mt-4">
                            The content on this Platform is <strong>not</strong>:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Medical, psychological, or legal advice</li>
                            <li>Pornographic or sexually explicit imagery</li>
                            <li>A matchmaking or dating service</li>
                            <li>A platform for sex work or commercial sexual services</li>
                        </ul>
                    </section>

                    {/* Section 4 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">4. Disclaimer of Liability</h2>
                        <p>
                            The information provided on this Platform is for <strong>general educational purposes only</strong>.
                            We make no representations or warranties about the accuracy, completeness, or suitability of
                            any information on the Platform.
                        </p>
                        <div className="bg-wine-900/30 border border-wine-500/30 rounded-lg p-4 mt-4">
                            <p className="text-wine-300 text-sm">
                                <strong>⚠️ Important:</strong> Some activities described on this Platform may carry inherent risks.
                                Always consult with qualified professionals before engaging in any activity that may affect your
                                health, safety, or well-being. We are not responsible for any injury, harm, or legal consequences
                                resulting from the use of information on this Platform.
                            </p>
                        </div>
                    </section>

                    {/* Section 5 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">5. Consent & Safety</h2>
                        <p>
                            All sexual activities should be conducted between consenting adults. We strongly emphasize:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Informed, enthusiastic consent is essential</li>
                            <li>Respect for boundaries and safe words</li>
                            <li>Understanding of local laws regarding sexual practices</li>
                            <li>Awareness that consent laws vary by jurisdiction</li>
                        </ul>
                    </section>

                    {/* Section 6 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">6. Intellectual Property</h2>
                        <p>
                            All content on this Platform, including text, graphics, logos, and software, is the property
                            of Pleasance or its content suppliers and is protected by intellectual property laws.
                        </p>
                    </section>

                    {/* Section 7 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">7. Modifications</h2>
                        <p>
                            We reserve the right to modify these Terms of Service at any time. Changes will be effective
                            immediately upon posting. Your continued use of the Platform after changes constitutes
                            acceptance of the modified terms.
                        </p>
                    </section>

                    {/* Section 8 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">8. Governing Law</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of Quebec, Canada,
                            without regard to its conflict of law provisions.
                        </p>
                    </section>

                    {/* Section 9 */}
                    <section>
                        <h2 className="text-2xl text-ivory-100 font-medium mb-4">9. Contact</h2>
                        <p>
                            For questions about these Terms of Service, please contact us at:
                            <span className="text-wine-400"> legal@pleasance.app</span>
                        </p>
                    </section>

                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-charcoal-700">
                    <p className="text-ivory-600 text-sm text-center">
                        © 2026 Pleasance. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default TermsPage;
